import { Component, OnInit } from '@angular/core';
import { BaseService } from '@core';
import { AccountDbQueryService, AccountDbSaveService } from '@account';
import { JobOrderNote, ProximityUser, ProximityUserNote } from '@models';
import { UtilFns } from '@utils';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';

export interface ProximityUserNoteDialogParams {
  proximityUser: ProximityUser;
  proximityUserNote?: ProximityUserNote; // will be undefined for new note
  // all below required for
  isEditable?: boolean; // ignored if proximityUserNote is undefined
}

@Component({
  selector: 'prox-proximity-user-note-dialog',
  templateUrl: './proximity-user-note-dialog.html',
  standalone: false,
})
export class ProximityUserNoteDialogComponent implements OnInit {
  UtilFns = UtilFns;
  params!: ProximityUserNoteDialogParams;

  proximityUserNote!: ProximityUserNote;
  isEditable!: boolean;

  isPageReady = false;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig<ProximityUserNoteDialogParams>,
    public dbQueryService: AccountDbQueryService,
    public dbSaveService: AccountDbSaveService,
    public baseService: BaseService,
  ) {}

  public static async openDialog(dialogService: DialogService, data: ProximityUserNoteDialogParams, config?: DynamicDialogConfig) {
    const ref = dialogService.open(ProximityUserNoteDialogComponent, {
      closable: false,
      draggable: true,
      modal: true,
      width: '80vh',
      height: '50vh',
      // baseZIndex: 10000,
      focusOnShow: false,
      ...config,
      data: data,
      header: `Note for Proximity User - ${data.proximityUser.fullName}`,
    });
    const r = await firstValueFrom(ref.onClose);
    return r as ProximityUserNote | undefined;
  }

  async ngOnInit() {
    this.params = this.config.data!;

    UtilFns.assertNonNull(this.params);

    // This avoids the ExpressionChangedAfterItHasBeenCheckedError - on the isEditable binding.
    await UtilFns.wait(0);

    if (this.params.proximityUserNote == null) {
      this.proximityUserNote = this.createProximityUserNote();
      this.isEditable = true;
    } else {
      this.proximityUserNote = this.params.proximityUserNote;
      this.isEditable = !!this.params.isEditable;
    }

    this.isPageReady = true;
  }

  createProximityUserNote() {
    const proxUserNote = this.dbSaveService.createEntity(ProximityUserNote, {
      proximityUserId: this.params.proximityUser.id,
      isSystemGenerated: false,
      noteTs: new Date(),
    });

    return proxUserNote;
  }

  async onOk() {
    this.proximityUserNote.note = this.proximityUserNote.note.trim();
    if (this.proximityUserNote.note.length == 0) {
      await this.baseService.dialogService.okDialog('Note is empty', 'Empty notes cannot be saved');
      return;
    }
    if (this.proximityUserNote.entityAspect.entityState.isAddedModifiedOrDeleted()) {
      await this.dbSaveService.saveSelectedChanges([this.proximityUserNote]);
    }
    this.ref.close(this.proximityUserNote);
  }

  onCancel() {
    this.proximityUserNote.entityAspect.rejectChanges();
    this.ref.close();
  }
}
